import { SelectOption } from "atoms/forms/select";
import { HubspotFormField } from "models/interfaces/hubspot-form-field";
import { HubspotFormSubmissionField } from "models/interfaces/hubspot-form-submission";
import HubspotFormFieldRecord from "models/view-models/hubspot-form-field-record";
import HubspotFormSubmissionFieldRecord from "models/view-models/hubspot-form-submission-field-record";
import HubspotFormSubmissionRecord from "models/view-models/hubspot-form-submission-record";
import { useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { t } from "utilities/localization-utils";
import HubspotFormService from "utilities/services/hubspot-form-service";
import StringUtils from "utilities/string-utils";

export type ChangePayload =
    | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    | SelectOption;

export const HubspotAutopopulatedFieldNames = {
    FirstName: "firstname",
    LastName: "lastname",
    Email: "email",
    TotalLicenses: "total_licenses",
};

export const useHubspotForm = (
    formId: string,
    defaultValues?: { [x: string]: string } | undefined
) => {
    const { globalState } = useGlobalState();
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [formSubmissionPayload, setFormSubmissionPayload] =
        useState<HubspotFormSubmissionRecord>();
    const [formErrors, setFormErrors] = useState<
        { [x: string]: string[] } | undefined
    >();
    const { get: getFormFields } = HubspotFormService.useGetQuery();
    const { create: submitForm } = HubspotFormService.useCreate();
    const { data, isLoading: isFormLoading } = getFormFields(
        { formId },
        undefined,
        {
            enabled:
                !!formId &&
                (!!globalState.currentIdentity?.userId() ||
                    !globalState.isAuthenticated()),
            onSuccess(data) {
                if (!formSubmissionPayload?.portalId) {
                    let fieldsToUpdate = initiateSubmissionRecord(
                        data?.resultObject?.fields ?? []
                    );
                    if (globalState.isAuthenticated()) {
                        fieldsToUpdate = autoPopulateFields(fieldsToUpdate);
                    }
                    const submission = new HubspotFormSubmissionRecord().with({
                        portalId: data.resultObject?.portalId,
                        fields: [...fieldsToUpdate],
                    });
                    setFormSubmissionPayload(submission);
                }
            },
        }
    );

    const fields = data?.resultObject?.fields ?? [];

    // For ease of access for a value of a given field
    const fieldValueMap = formSubmissionPayload?.fields.reduce((obj, field) => {
        return { ...obj, [field.name]: field.value ?? "" };
    }, {});

    const handleSubmissionChange = (fieldName: string, value: string) => {
        setFormSubmissionPayload((prev) =>
            formSubmissionPayload?.with({
                fields:
                    prev?.fields.map((field) =>
                        field.name === fieldName ? field.with({ value }) : field
                    ) ?? [],
            })
        );
    };
    const handleFieldChange = (fieldName: string, event: ChangePayload) => {
        setFormErrors((prev) => ({ ...prev, [fieldName]: [] }));
        if ("target" in event) {
            handleSubmissionChange(fieldName, event.target.value);
        } else {
            handleSubmissionChange(fieldName, event.value);
        }
    };

    const handleSubmit = async (): Promise<boolean> => {
        setIsFormSubmitting(true);
        try {
            const { isErrored, formErrors } = validateForm();
            if (isErrored) {
                setFormErrors(formErrors);
                return false;
            }
            const response = await submitForm(formSubmissionPayload!, {
                formId,
                portalId: formSubmissionPayload?.portalId!,
            });
            if (!response) {
                throw new Error();
            }
            setIsFormSubmitting(false);
            return true;
        } catch (error) {
            return false;
        }
    };

    const autoPopulateFields = (fields: HubspotFormSubmissionFieldRecord[]) => {
        // Record to hold changes
        const formFieldsWithDefaultValues = fields.map((field) => {
            const defaultValue = defaultValues?.[field.name];
            return !!defaultValue
                ? field.with({
                      value: defaultValue,
                  })
                : field;
        });
        const formFieldsWithUserInfo = formFieldsWithDefaultValues.map(
            (field) => {
                switch (field.name) {
                    case HubspotAutopopulatedFieldNames.FirstName:
                        return field.with({
                            value: globalState?.currentIdentity?.user
                                ?.firstName,
                        });
                    case HubspotAutopopulatedFieldNames.LastName:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.lastName,
                        });
                    case HubspotAutopopulatedFieldNames.Email:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.email,
                        });
                    default:
                        return field;
                }
            }
        );

        if (
            globalState?.currentIdentity?.isConfiguredWithActiveSubscription()
        ) {
            const totalLicenses = globalState?.currentIdentity?.isGroupMember()
                ? `${globalState?.currentIdentity?.getCurrentTeam()?.totalLicenses}`
                : "1";
            const formFieldsWithUserInfoAndLicenses =
                formFieldsWithUserInfo.map((field) =>
                    field.name === HubspotAutopopulatedFieldNames.TotalLicenses
                        ? field.with({ value: totalLicenses })
                        : field
                );

            return formFieldsWithUserInfoAndLicenses;
        } else {
            const formDataWithUserInfoAndZeroLicenses =
                formFieldsWithUserInfo.map((field) =>
                    field.name === HubspotAutopopulatedFieldNames.TotalLicenses
                        ? field.with({ value: "0" })
                        : field
                );
            return formDataWithUserInfoAndZeroLicenses;
        }
    };

    const validateField = (
        submissionField: HubspotFormSubmissionField,
        sourceField: HubspotFormField
    ) => {
        const errors: string[] = [];
        if (sourceField.required && !submissionField.value) {
            errors.push(t("propertyIsRequired", { name: sourceField.label }));
        }
        if (
            sourceField.name === HubspotAutopopulatedFieldNames.Email &&
            !StringUtils.isValidEmail(submissionField.value)
        ) {
            errors.push(t("invalid-email-field"));
        }

        return errors;
    };
    const validateForm = () => {
        let isErrored = false;
        const formErrors = formSubmissionPayload?.fields
            ?.map((submissionField) => {
                const matchingSourceField = fields.find(
                    (sourceField) => sourceField.name === submissionField.name
                );
                const errors = validateField(
                    submissionField,
                    matchingSourceField!
                );
                if (CollectionUtils.isNotEmpty(errors)) {
                    isErrored = true;
                }
                return {
                    name: submissionField.name,
                    errors: errors,
                };
            })
            .reduce((acc, field) => {
                acc[field.name] = field.errors;
                return acc;
            }, {});

        return { isErrored, formErrors };
    };

    return {
        fields,
        fieldValueMap,
        isFormLoading,
        isFormSubmitting,
        onFieldChange: handleFieldChange,
        onSubmit: handleSubmit,
        formErrors,
    };
};

const initiateSubmissionRecord = (
    fields: HubspotFormFieldRecord[]
): HubspotFormSubmissionFieldRecord[] =>
    fields.map((field) => {
        return new HubspotFormSubmissionFieldRecord().with({
            value: "",
            name: field.name,
            objectTypeId: field.objectTypeId,
        });
    });
